import React, { useEffect, useRef, useState } from "react";
import { Alert, Badge, Button, Skeleton } from "stories/Admin";
import useFetch from "hooks/useFetch";
import PageModal from "../LocationsPage/PageModal";
import styles from "./ExternalPage.module.scss";
import { SYNC_ACTION_LABELS, SyncDetails } from "./bonnetidTypes";

interface SyncDetailsDialogProps {
  /** Loggraden som skal vises, null = lukket. */
  logId: number | null;
  title?: string;
  onClose: () => void;
}

const PRAYERS = ["Fajr", "Dhuhr", "Asr", "Maghrib", "Isha"];

const tone = (action: string) =>
  action === "CREATED" || action === "REPLACED"
    ? "mint"
    : action === "SKIPPED_SAME"
      ? "grey"
      : "gold";

const offsetText = (minutes: number | undefined) =>
  minutes == null || minutes < 0 ? "–" : `+${minutes} min`;

/**
 * «Vis hva som ble hentet»: svaret fra Bonnetid for en manuell synk av én moské, jamat-perioden den brukte og hva
 * som skjedde med hver dag (lagt inn, erstattet eller beholdt, og hvorfor).
 */
const SyncDetailsDialog: React.FC<SyncDetailsDialogProps> = ({
  logId,
  title,
  onClose,
}) => {
  const { execute, lastError } = useFetch<SyncDetails>();
  const executeRef = useRef(execute);
  executeRef.current = execute;
  const lastErrorRef = useRef(lastError);
  lastErrorRef.current = lastError;
  const [details, setDetails] = useState<SyncDetails | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (logId == null) return;
    let active = true;
    setDetails(null);
    setError("");
    executeRef
      .current(`/api/v1/admin/external-jamat/sync-logs/${logId}/details`, "GET")
      .then((res) => {
        if (!active) return;
        if (res) setDetails(res as SyncDetails);
        else
          setError(
            lastErrorRef.current()?.message || "Kunne ikke hente detaljene.",
          );
      });
    return () => {
      active = false;
    };
  }, [logId]);

  const period = details?.period;
  const raw =
    details?.response != null ? JSON.stringify(details.response, null, 2) : "";

  return (
    <PageModal
      open={logId != null}
      title={title ?? "Hva synken hentet"}
      icon="doc"
      width={820}
      onClose={onClose}
      footer={<Button onClick={onClose}>Lukk</Button>}
    >
      {error && <Alert tone="error">{error}</Alert>}
      {!details && !error && <Skeleton height={160} radius={16} />}
      {details && (
        <div className={styles.dialogBody}>
          <p className={styles.muted}>
            {details.sourceOrgNr !== details.orgNr
              ? `${details.orgNr} henter fra Bonnetid-ID ${details.sourceOrgNr}. `
              : ""}
            <code>{details.request}</code> (gammelt Bønnetid-API, det
            jamat-synken bruker)
          </p>

          {period ? (
            <div className={styles.syncPeriod}>
              <b>
                Jamat-periode {period.startDate} – {period.endDate}
              </b>
              <table className={styles.syncTable}>
                <thead>
                  <tr>
                    <th />
                    {PRAYERS.map((p) => (
                      <th key={p}>{p}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th>Tid</th>
                    {[
                      period.fajr,
                      period.duhr,
                      period.asr,
                      period.maghrib,
                      period.isha,
                    ].map((t, i) => (
                      <td key={i}>{t || "–"}</td>
                    ))}
                  </tr>
                  <tr>
                    <th>Offset</th>
                    {["fajr", "duhr", "asr", "maghrib", "isha"].map((k) => (
                      <td key={k}>{offsetText(period.offsets[k])}</td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <Alert tone="warn">
              Bonnetid har ingen gyldig jamat-periode for denne moskeen, så
              ingen jamat-tider ble lagt inn.
            </Alert>
          )}

          {Object.keys(details.summary).length > 0 && (
            <div className={styles.rowActions}>
              {Object.entries(details.summary).map(([action, count]) => (
                <Badge key={action} tone={tone(action)}>
                  {SYNC_ACTION_LABELS[action] ?? action}: {count}
                </Badge>
              ))}
            </div>
          )}

          {details.days.length > 0 && (
            <div className={styles.syncDays}>
              <table className={styles.syncTable}>
                <thead>
                  <tr>
                    <th>Dato</th>
                    <th>Resultat</th>
                    <th>Lå der fra før</th>
                  </tr>
                </thead>
                <tbody>
                  {details.days.map((d) => (
                    <tr key={d.date}>
                      <td>{d.date}</td>
                      <td>
                        <Badge tone={tone(d.action)}>
                          {SYNC_ACTION_LABELS[d.action] ?? d.action}
                        </Badge>
                      </td>
                      <td className={styles.muted}>
                        {d.existing
                          ? `${d.existingSource ?? "?"}: ${d.existing.map((t) => t || "–").join(" · ")}`
                          : "–"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <details>
            <summary>Svaret fra Bonnetid (JSON)</summary>
            {raw ? (
              <pre className={styles.rawJson}>{raw}</pre>
            ) : (
              <p className={styles.muted}>Ingen svar.</p>
            )}
          </details>
        </div>
      )}
    </PageModal>
  );
};

export default SyncDetailsDialog;
